<template>
  <div class="tw-bg-white tw-rounded-lg tw-shadow-sm tw-border tw-border-gray-200">
    <div class="tw-flex tw-flex-row tw-items-center tw-justify-between tw-space-y-0 tw-pb-2 tw-p-6">
      <div class="tw-flex tw-items-center tw-gap-2">
        <Users class="tw-w-5 tw-h-5 tw-text-gray-600" />
        <h3 class="tw-text-lg tw-font-semibold tw-text-gray-900">
          {{ t('customer_management.assign_team.title') }}
        </h3>
      </div>
    </div>

    <div class="tw-px-6 tw-pb-6 tw-space-y-6">
      <!-- Purchasers & Payers -->
      <div class="tw-grid tw-grid-cols-1 md:tw-grid-cols-2 tw-gap-6">
        <!-- Purchasers -->
        <div class="tw-bg-gray-50 tw-rounded-lg tw-p-4 tw-shadow-sm tw-border tw-border-gray-100">
          <h4 class="tw-text-sm tw-font-medium tw-text-gray-700 tw-mb-2">
            {{ t('customer_management.assign_team.assigned_purchasers') }} ({{
              team?.purchasers?.length || 0
            }})
          </h4>
          <div class="tw-flex tw-flex-wrap tw-gap-2">
            <Badge v-for="userId in team?.purchasers || []" :key="userId" variant="secondary">
              {{ getUserName(userId) }}
            </Badge>
            <span v-if="!team?.purchasers?.length" class="tw-text-sm tw-text-gray-600">--</span>
          </div>
        </div>

        <!-- Payers -->
        <div class="tw-bg-gray-50 tw-rounded-lg tw-p-4 tw-shadow-sm tw-border tw-border-gray-100">
          <h4 class="tw-text-sm tw-font-medium tw-text-gray-700 tw-mb-2">
            {{ t('customer_management.assign_team.assigned_payers') }} ({{
              team?.payers?.length || 0
            }})
          </h4>
          <div class="tw-flex tw-flex-wrap tw-gap-2">
            <Badge v-for="userId in team?.payers || []" :key="userId" variant="secondary">
              {{ getUserName(userId) }}
            </Badge>
            <span v-if="!team?.payers?.length" class="tw-text-sm tw-text-gray-600">--</span>
          </div>
        </div>
      </div>

      <!-- Order Clearing Groups (full width) -->
      <div>
        <ClearingGroupsSection
          :groups="orderClearingGroups"
          :all-users="allUsersList"
          :title="t('customer_management.assign_team.order_clearing_groups')"
          :readonly="true"
        />
      </div>

      <!-- Payment Clearing Groups (full width) -->
      <div>
        <ClearingGroupsSection
          :groups="paymentClearingGroups"
          :all-users="allUsersList"
          :title="t('customer_management.assign_team.payment_clearing_groups')"
          :readonly="true"
        />
      </div>

      <!-- Approvers & Viewers -->
      <div class="tw-grid tw-grid-cols-1 md:tw-grid-cols-2 tw-gap-6">
        <!-- Approvers -->
        <div class="tw-bg-gray-50 tw-rounded-lg tw-p-4 tw-shadow-sm tw-border tw-border-gray-100">
          <h4 class="tw-text-sm tw-font-medium tw-text-gray-700 tw-mb-2">
            {{ t('customer_management.assign_team.assigned_approvers') }} ({{
              team?.approvers?.length || 0
            }})
          </h4>
          <div class="tw-flex tw-flex-wrap tw-gap-2">
            <Badge
              v-for="approver in team?.approvers || []"
              :key="approver.userId"
              variant="secondary"
              class="tw-gap-1"
            >
              {{ getUserName(approver.userId) }}
              <span class="tw-text-xs tw-opacity-70">
                ({{
                  approver.limit && approver.limit !== 'None'
                    ? formatCurrency(Number(approver.limit))
                    : '-'
                }})
              </span>
            </Badge>
            <span v-if="!team?.approvers?.length" class="tw-text-xs tw-text-gray-600">--</span>
          </div>
        </div>

        <!-- Viewers -->
        <div class="tw-bg-gray-50 tw-rounded-lg tw-p-4 tw-shadow-sm tw-border tw-border-gray-100">
          <h4 class="tw-text-sm tw-font-medium tw-text-gray-700 tw-mb-2">
            {{ t('customer_management.assign_team.assigned_viewers') }} ({{
              team?.viewers?.length || 0
            }})
          </h4>
          <div class="tw-flex tw-flex-wrap tw-gap-2">
            <Badge v-for="userId in team?.viewers || []" :key="userId" variant="secondary">
              {{ getUserName(userId) }}
            </Badge>
            <span v-if="!team?.viewers?.length" class="tw-text-xs tw-text-gray-600">--</span>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref, onMounted } from 'vue';
import { Users } from 'lucide-vue-next';
import { useI18n } from 'vue-i18n';
import { useFormatValue } from '@/_helpers/composables/formatting.composable';
import { Badge } from '@/components/ui/badge';
import { useCustomerStore } from '@/stores/customer.store';
import ClearingGroupsSection from '../Forms/ClearingGroupsSection.vue';

interface TeamMember {
  userId: string | number;
  limit?: number | string;
}

interface Team {
  purchasers?: (string | number)[];
  payers?: (string | number)[];
  approvers?: TeamMember[];
  viewers?: (string | number)[];
}

interface ClearingGroup {
  id?: number;
  name: string;
  display_order: number;
  assignees: (number | string)[];
}

interface ClearingUser {
  id: number | string;
  display_name: string;
  email: string;
}

interface Customer {
  team?: Team;
  [key: string]: any; // Allow additional properties
}

interface Props {
  customer: Customer;
  customerId: number;
}

const props = defineProps<Props>();
const { t } = useI18n();
const { formatCurrency } = useFormatValue();
const { getAssignTeamById, getUsers, getClearingGroups } = useCustomerStore();

// Local state for team data
const teamData = ref<Team>({
  purchasers: [],
  payers: [],
  approvers: [],
  viewers: [],
});

// Clearing groups state
const orderClearingGroups = ref<ClearingGroup[]>([]);
const paymentClearingGroups = ref<ClearingGroup[]>([]);
const allUsersList = ref<ClearingUser[]>([]);

// User management
const usersMap = ref(new Map());

const getUserName = (userId: string | number): string => {
  const user = usersMap.value.get(String(userId));
  if (user) {
    // Handle different user name formats
    if (user.first_name && user.last_name) {
      return `${user.first_name} ${user.last_name}`;
    } else if (user.display_name) {
      return user.display_name;
    } else if (user.email) {
      return user.email;
    }
  }
  return `User ${userId}`;
};

const getTeamData = async () => {
  try {
    const teamResponse = await getAssignTeamById({
      customer: props.customerId,
      is_active: true,
    });

    // Transform team data to match component interface
    const transformedTeam: Team = {
      purchasers: [],
      payers: [],
      approvers: [],
      viewers: [],
    };

    teamResponse.results?.forEach((assignment: any) => {
      const userId = assignment.user?.id || assignment.user;
      const userInfo = assignment.user;

      // Store user info in the map if it's a full user object
      if (typeof userInfo === 'object' && userInfo.id) {
        usersMap.value.set(String(userInfo.id), userInfo);
      }

      // Map user role to appropriate arrays based on the API structure
      const userRole = assignment.user_role;

      switch (userRole) {
        case 'PURCHASER':
          transformedTeam.purchasers!.push(userId);
          break;
        case 'PAYER':
          transformedTeam.payers!.push(userId);
          break;
        case 'APPROVER':
          transformedTeam.approvers!.push({
            userId,
            limit: assignment.approver_limit,
          });
          break;
        case 'VIEWER':
          transformedTeam.viewers!.push(userId);
          break;
      }
    });

    teamData.value = transformedTeam;
  } catch (error) {
    console.error('Error fetching team data:', error);
  }
};

const getAllUsers = async () => {
  try {
    const users = await getUsers();
    users.forEach((user: any) => {
      usersMap.value.set(String(user.id), user);
    });
    allUsersList.value = users.map((user: any) => ({
      id: user.id,
      display_name: user.display_name || `${user.first_name} ${user.last_name}`.trim(),
      email: user.email,
    }));
  } catch (error) {
    console.error('Error fetching users:', error);
  }
};

const getClearingGroupsData = async () => {
  try {
    const res = await getClearingGroups(props.customerId);
    orderClearingGroups.value = res.order_groups || [];
    paymentClearingGroups.value = res.payment_groups || [];
  } catch (error) {
    console.error('Error fetching clearing groups:', error);
  }
};

// Use team data from props if available, otherwise use local data
const team = computed(() => (props.customer as any).team || teamData.value);

onMounted(async () => {
  // Only fetch if team data is not provided via props
  if (!(props.customer as any).team) {
    await Promise.all([getAllUsers(), getTeamData(), getClearingGroupsData()]);
  }
});
</script>
